import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { getTierById } from '../../config/subscriptionTiers'
import routes from '../../pages/routes'
import { createCheckoutSession } from '../../services/subscriptionService'
import { sessionState } from '../../state'
import Database from '../../types/Database'

interface OfficeSelectorProps {
  open: boolean
  tierId: string
  billingInterval: 'monthly' | 'annual'
  userId: string
  onClose: () => void
}

export const OfficeSelector = ({
  open,
  tierId,
  billingInterval,
  userId,
  onClose
}: OfficeSelectorProps) => {
  const navigate = useNavigate()
  const session = useRecoilValue(sessionState)
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [checkoutLoading, setCheckoutLoading] = useState(false)

  const tier = getTierById(tierId as Database.SubscriptionTier)

  // Get offices owned by the user from session state
  const offices = (session.offices || []).filter(o => o.uid === userId)

  useEffect(() => {
    // Auto-select first office if only one
    if (offices.length === 1 && offices[0]?.oid) {
      setSelectedOfficeId(offices[0].oid)
    }
  }, [offices])

  const handleContinue = async () => {
    if (!selectedOfficeId) return

    try {
      setCheckoutLoading(true)
      setError(null)

      // Create Stripe Checkout Session
      const checkoutUrl = await createCheckoutSession(
        selectedOfficeId,
        tierId as Database.SubscriptionTier,
        billingInterval
      )

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl
    } catch (err) {
      console.error('Error creating checkout session:', err)
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to start checkout. Please try again.'
      )
      setCheckoutLoading(false)
    }
  }

  const handleCreateOffice = () => {
    navigate(routes.createOffice)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Typography variant='h5' component='div' fontWeight={600}>
          Select Office
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Choose which office to apply the {tier?.name} plan to
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {offices.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
              You don't have any offices yet. Create one to get started!
            </Typography>
            <Button
              variant='contained'
              color='primary'
              onClick={handleCreateOffice}
            >
              Create Office
            </Button>
          </Box>
        ) : (
          <FormControl component='fieldset' fullWidth>
            <RadioGroup
              value={selectedOfficeId}
              onChange={e => setSelectedOfficeId(e.target.value)}
            >
              {offices.map(office => (
                <Box
                  key={office.oid}
                  sx={{
                    border: '1px solid',
                    borderColor:
                      selectedOfficeId === office.oid
                        ? 'primary.main'
                        : 'divider',
                    borderRadius: 1,
                    p: 2,
                    mb: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onClick={() => office.oid && setSelectedOfficeId(office.oid)}
                >
                  <FormControlLabel
                    value={office.oid || ''}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant='body1' fontWeight={600}>
                          {office.name}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Current plan:{' '}
                          {getTierById(office.subscriptionTier || 'free')?.name}
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', m: 0 }}
                  />
                </Box>
              ))}
            </RadioGroup>
          </FormControl>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color='inherit' disabled={checkoutLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleContinue}
          variant='contained'
          color='primary'
          disabled={!selectedOfficeId || checkoutLoading}
          startIcon={
            checkoutLoading ? <CircularProgress size={20} /> : undefined
          }
        >
          {checkoutLoading ? 'Loading...' : 'Continue to Checkout'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
